import React from 'react';
import { TVoiceModel } from '@/types/TVoiceModel';
import { SettingFilled, InfoCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';

const DEFAULT_DESCRIPTIONS = [
	'Professional voice with clear articulation and natural flow.',
	'Engaging tone perfect for business communications.',
	'Clear and friendly voice ideal for customer interactions.',
];

interface VoiceInfoProps {
	data: TVoiceModel;
	loggedInUser: any;
	isSelected?: boolean;
	onSettingsClick?: () => void;
	showSettings?: boolean;
	voiceModalCredits?: Record<string, number> | null;
	fromConfig?: boolean;
}

export const VoiceInfo: React.FC<VoiceInfoProps> = ({
	data,
	loggedInUser,
	isSelected,
	onSettingsClick,
	showSettings,
	voiceModalCredits,
	fromConfig,
}) => {
	const resellerData = useAppSelector(getResellerData);
	const getInitials = (name: string) => {
		if (!name || typeof name !== 'string') {
			return 'V';
		}

		const firstWord = name.split(' ')[0];
		return firstWord.substring(0, 2).toUpperCase();
	};

	const getRandomDescription = () => {
		return DEFAULT_DESCRIPTIONS[Math.floor(Math.random() * DEFAULT_DESCRIPTIONS.length)];
	};

	return (
		<div className="p-2 sm:p-3 group hover:bg-blue-50/30 transition-colors rounded-lg">
			{/* First Row: Initials, Name */}
			<div className="flex items-start gap-2 sm:gap-3">
				{/* Initials */}
				<div
					className={`
            w-9 h-9 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center
            bg-gray-100 flex-shrink-0
            ${isSelected ? 'ring-2 ring-blue-400' : ''}
          `}
				>
					<span className="text-base sm:text-lg font-semibold text-gray-800">
						{getInitials(data.name)}
					</span>
				</div>

				{/* Name, Active Badge and Settings */}
				<div className="flex flex-col flex-1 min-w-0">
					<div className="flex items-center justify-between w-full">
						<div className="flex flex-col">
							<h1 className="font-medium text-gray-900 text-sm mb-1 sm:text-[15px] truncate max-w-[140px] sm:max-w-[180px]">
								{data?.name || 'Voice'}
							</h1>
							{/* Recommended/Not Recommended Badge */}
							{data?.provider === '11labs' && (
								<div
									className={`
                  text-xs px-2 py-0.5 rounded-md w-fit
                  ${data.provider === '11labs' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                `}
								>
									Recommended
								</div>
							)}
						</div>

						{showSettings && (
							<button
								onClick={onSettingsClick}
								className="p-1 sm:p-1.5 hover:bg-gray-100 rounded-md transition-colors"
							>
								<SettingFilled className="text-gray-400 hover:text-gray-600 text-sm sm:text-base" />
							</button>
						)}
					</div>
					{/* Active Badge right after name with reduced spacing */}
					{isSelected && (
						<div className="flex items-center gap-1 mt-2 bg-green-50 text-green-600 rounded-full text-xs font-medium w-fit">
							<CheckCircleFilled className="text-xs" />
							<span>Active</span>
						</div>
					)}
				</div>
			</div>

			{/* Tags Row */}
			{!fromConfig && (
				<div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-2 sm:mt-3 mb-1 sm:mb-2">
					<span className="px-2 py-0.5 bg-white border border-gray-200 text-gray-700 rounded-full text-xs font-medium truncate max-w-[90px]">
						{data?.provider}
					</span>
					<span className="px-2 py-0.5 bg-white border border-gray-200 text-gray-700 rounded-full text-xs font-medium truncate max-w-[90px]">
						{data.language || 'English'}
					</span>
					{data?.gender && (
						<span className="px-2 py-0.5 bg-white border border-gray-200 text-gray-700 rounded-full text-xs font-medium truncate max-w-[90px]">
							{data.gender}
						</span>
					)}
				</div>
			)}

			{/* Description with Tooltip */}
			{!fromConfig && (
				<div className="h-[40px] mt-2 sm:mt-3 mb-2 sm:mb-3">
					<Tooltip title={data?.description || getRandomDescription()} mouseEnterDelay={0.5}>
						<p className="text-xs sm:text-sm text-gray-500 line-clamp-2 cursor-help">
							{data?.description || getRandomDescription()}
						</p>
					</Tooltip>
				</div>
			)}

			{/* Credits Row */}
			{!fromConfig &&
				loggedInUser.resellerClient &&
				!!resellerData.stripe_connect_id &&
				!!loggedInUser.reseller_email &&
				!!loggedInUser?.subscribedProductId &&
				loggedInUser?.subscriptionStatus !== 'canceled' &&
				voiceModalCredits &&
				data.provider && (
					<div className="flex items-center gap-1.5">
						<span className="text-sm font-medium text-gray-800 whitespace-nowrap">
							{`${voiceModalCredits[data.provider.toLowerCase()]} credits/min`}
						</span>
						<Tooltip title="Credits are consumed per minute of generated audio">
							<InfoCircleOutlined className="text-xs sm:text-sm text-gray-400" />
						</Tooltip>
					</div>
				)}

			{!fromConfig && !loggedInUser.resellerClient && (
				<div className="flex items-center gap-1">
					<span className="text-xs sm:text-sm font-medium text-gray-800 whitespace-nowrap">
						1 credit/min
					</span>
					<Tooltip title="Credits are consumed per minute of generated audio">
						<InfoCircleOutlined className="text-xs sm:text-sm text-gray-400" />
					</Tooltip>
				</div>
			)}
		</div>
	);
};

export default VoiceInfo;
